import { Formik, Field, Form } from "formik";
import { FC, useState, useEffect, useLayoutEffect } from "react";
import {
    FeatureList,
    Permissions,
    Product,
    Project,
    ProjectList,
    StateActionPermission,
    TypeAction,
} from "../../../models/models";
import {
    useUpdateProductMutation,
    useCreateProductMutation,
    useGetProductInfoQuery,
    useToggleProductStatusMutation,
} from "../../../redux/RTKQuery/api";
import ActionButtons from "../../../assets/components/ActionButtons/ActionButtons";
import "./ProductsSubRow.scss";
import TextInput from "../../../assets/components/Inputs/TextInput/TextInput";
import NumberInput from "../../../assets/components/Inputs/NumberInput/NumberInput";
import * as Yup from "yup";
import Checkbox from "../../../assets/components/Checkbox/Checkbox";
import TextArea from "../../../assets/components/TextArea/TextArea";
import Select from "../../../assets/components/Select/Select";
import SelectCheckboxList, {
    OnChangeStateHandlerProps,
} from "../../../assets/components/SelectCheckboxList/SelectCheckboxList";
import {
    getAvailableProjectFeatures,
    prepareDataForSelectCheckbox,
} from "../helpers";
import {
    OptionWCheckboxList,
    hasPermission,
    getActiveOptions,
} from "../../../assets/helpers/helpers";
import { useAppDispatch, useAppSelector } from "../../../assets/hooks/hooks";
import { useNotification } from "../../../assets/hooks/useNotification";
import { actionText } from "../../../text";
import ActualInformPanel from "../InformPanel/ActualInformPanel";
import { uiSlicedActions } from "../../../redux/ui-slice";
import { toast } from "react-toastify";

const validateSchema = Yup.object().shape({
    days: Yup.number()
        .positive("Only positive days")
        .min(0, "Too Short!")
        .max(365, "Too Long!")
        .integer("Only integer numbers!")
        .required("Required"),
    gracePeriod: Yup.number()
        .positive("Only positive number!")
        .min(0, "Too Short!")
        .max(100, "Too Long!")
        .integer("Only integer numbers!")
        .required("Required"),
    priority: Yup.number()
        .positive("Only positive number!")
        .min(0, "Too Short!")
        .max(5, "Too Long!")
        .integer("Only integer numbers!")
        .required("Required"),
    name: Yup.string().required("Required"),
    title: Yup.string().required("Required"),
});

type formField = {
    id?: number;
    days: number | null;
    name: string;
    title: string;
    hasLifeTime: boolean;
    isFree: boolean;
    isTrial: boolean;
    isDisabled: boolean;
    gracePeriod: number;
    priority: number;
};

export interface EditRowProduct extends Product {
    projects?: ProjectList;
    closeFormHandler: (id: number) => void;
}

const ProductsSubRow: FC<EditRowProduct> = ({
    name,
    description,
    days,
    hasLifeTime,
    isFree,
    title,
    isTrial,
    isDisabled,
    id,
    priority,
    gracePeriod,
    projectId: productProjectId,
    features = [],
    projects = [],
    // InformPanel
    closeFormHandler,
}) => {
    const dispatch = useAppDispatch();

    const [updateProduct] = useUpdateProductMutation({
        fixedCacheKey: "update_actual_product",
    });
    const [createProduct] = useCreateProductMutation({
        fixedCacheKey: "create_actual_product",
    });
    const { ConfirmationMessageBox } = useNotification();

    const [typeAction] = useState<TypeAction>(
        id === -1 ? { type: "create" } : { type: "update" }
    );

    const [activeFeatures, setActiveFeatures] = useState<FeatureList>(features);
    const [optionList, setOptionList] = useState<OptionWCheckboxList>([]);

    const {
        data: productInfoData,
        isLoading: isLoadingProductInfo,
        isSuccess: isSuccessProductInfo,
        isError: isErrorProductInfo,
    } = useGetProductInfoQuery({ id: id! });

    dispatch(
        uiSlicedActions.setLoading(isLoadingProductInfo ? "pending" : "hidden")
    );

    useLayoutEffect(() => {
        if (isErrorProductInfo) {
            toast.error("Error fetching product info");
        }
    }, [productInfoData, isSuccessProductInfo, isErrorProductInfo]);

    // The projects list can still be empty on first render (query in flight,
    // or nothing returned) — 0 keeps the Select unset instead of crashing.
    const [projectId, setProjectId] = useState<number>(
        productProjectId === 0 ? projects[0]?.id ?? 0 : productProjectId
    );
    const [countActiveFeatures, setCountActiveFeatures] = useState(0);

    // Projects can arrive after the first render — pick the first one so a
    // product with no project set is not saved with projectId 0.
    useEffect(() => {
        if (projectId === 0 && projects.length > 0) {
            setProjectId(projects[0].id);
        }
    }, [projectId, projects]);

    const user = useAppSelector((state) => state.initializeApp);
    const isAccessPermission = hasPermission(
        user.permissions,
        Permissions.PRODUCT_EDIT
    );

    useEffect(() => {
        const currentProjectFeatures = getAvailableProjectFeatures(
            projectId,
            projects
        );

        const optionList = prepareDataForSelectCheckbox(
            currentProjectFeatures,
            activeFeatures
        );
        setOptionList(optionList);

        const currentActiveFeatures = getActiveOptions(optionList);
        setCountActiveFeatures(currentActiveFeatures.length);
    }, [
        projects,
        projectId,
        features,
        activeFeatures,
        setProjectId,
        typeAction,
        productProjectId,
    ]);

    const onChangeProjectHandler = (
        currentId: number,
        currentName: string
    ): void => {
        setProjectId(currentId);

        const currentProjectFeatures = getAvailableProjectFeatures(
            currentId,
            projects
        );

        const optionList = prepareDataForSelectCheckbox(
            currentProjectFeatures,
            activeFeatures
        );
        setOptionList(optionList);

        const currentActiveFeatures = getActiveOptions(optionList);
        setCountActiveFeatures(currentActiveFeatures.length);
    };

    const onChangeStateOptionHandler = ({
        currentId,
        currentState,
    }: OnChangeStateHandlerProps) => {
        const updatedListOptions = optionList.map((option) =>
            currentId === option.id
                ? { ...option, checked: !currentState }
                : option
        );

        setOptionList(updatedListOptions);

        const currentActiveFeatures = getActiveOptions(updatedListOptions);

        setActiveFeatures(
            currentActiveFeatures as { id: number; name: string }[]
        );

        setCountActiveFeatures(currentActiveFeatures.length);
    };

    const submitHandler = async (values: formField) => {
        const dataForExport = {
            ...values,
            days: values.days === null ? 0 : values.days,
            features: activeFeatures,
        };

        ConfirmationMessageBox({
            textContent: actionText(
                typeAction.type as StateActionPermission,
                "product"
            ),
        }).then((value) => {
            if (value === "yes") {
                if (typeAction.type === "create") {
                    delete dataForExport.id;
                    createProduct(dataForExport);
                } else if (typeAction.type === "update") {
                    updateProduct(dataForExport);
                }
            }
        });
    };

    return (
        <div>
            <ActualInformPanel
                productName={productInfoData?.productName || ""}
                activatedLicenses={productInfoData?.activatedLicenses || 0}
                expiredLicenses={productInfoData?.expiredLicenses || 0}
                gracePeriodLicenses={productInfoData?.gracePeriodLicenses || 0}
                soldLicenses={productInfoData?.soldLicenses || 0}
                totalLicenses={productInfoData?.totalLicenses || 0}
                unsoldLicenses={productInfoData?.unsoldLicenses || 0}
            />
            <Formik
                initialValues={{
                    id,
                    name,
                    title,
                    description,
                    hasLifeTime,
                    isFree,
                    isTrial,
                    isDisabled,
                    days: days === null ? 0 : days,
                    projectId,
                    priority,
                    gracePeriod,
                }}
                enableReinitialize
                validationSchema={validateSchema}
                onSubmit={submitHandler}
            >
                {({ isSubmitting, isValid, values }) => {
                    return (
                        <Form className="form_products">
                            <div className="form_products__row">
                                <div className="form_products__col">
                                    <Select<Project>
                                        label="Name Project"
                                        options={projects}
                                        currentId={projectId}
                                        onChange={onChangeProjectHandler}
                                        readOnly={!isAccessPermission}
                                    />

                                    <SelectCheckboxList
                                        label="Features"
                                        name="features"
                                        placeholder="Please select features"
                                        countSelected={countActiveFeatures}
                                        options={optionList}
                                        onChange={onChangeStateOptionHandler}
                                        readOnly={!isAccessPermission}
                                    />

                                    <NumberInput
                                        label="Day:"
                                        name="days"
                                        min={1}
                                        max={365}
                                        disabled={values.hasLifeTime}
                                        readOnly={!isAccessPermission}
                                    />
                                </div>

                                <div className="form_products__col">
                                    <Field
                                        id="name"
                                        label="Activation Product Name *"
                                        name="name"
                                        as={TextInput}
                                        readOnly={!isAccessPermission}
                                    />

                                    <Field
                                        id="title"
                                        label="Display Product Name *"
                                        name="title"
                                        as={TextInput}
                                        readOnly={!isAccessPermission}
                                    />

                                    <NumberInput
                                        label="Grace Period"
                                        name="gracePeriod"
                                        min={0}
                                        max={100}
                                        readOnly={!isAccessPermission}
                                    />
                                </div>
                                <div className="form_products__col">
                                    <Field
                                        as={TextArea}
                                        id="description"
                                        name="description"
                                        mixin="products"
                                        label="Description"
                                        readOnly={!isAccessPermission}
                                    />

                                    <NumberInput
                                        label="Priority"
                                        name="priority"
                                        min={0}
                                        max={100}
                                        readOnly={!isAccessPermission}
                                    />
                                </div>
                            </div>

                            <div className="form_products__row">
                                <div className="form_products__col">
                                    <Field
                                        id="hasLifeTime"
                                        name="hasLifeTime"
                                        label="Lifetime"
                                        as={Checkbox}
                                        readOnly={!isAccessPermission}
                                    />
                                    <Field
                                        id="isFree"
                                        name="isFree"
                                        label="Is Free"
                                        as={Checkbox}
                                        readOnly={!isAccessPermission}
                                    />
                                    <Field
                                        id="isTrial"
                                        name="isTrial"
                                        label="is Trial"
                                        as={Checkbox}
                                        readOnly={!isAccessPermission}
                                    />
                                    <Field
                                        id="isDisabled"
                                        name="isDisabled"
                                        label="Is Disabled"
                                        as={Checkbox}
                                        readOnly={!isAccessPermission}
                                    />
                                </div>
                            </div>

                            <div className="form_products__footer">
                                <ActionButtons
                                    className="form_products__button_submit"
                                    actionButtons={[
                                        {
                                            type: "button",
                                            disabled: isSubmitting,
                                            name: "close",
                                            role: "secondary",
                                            onClick: () => {
                                                closeFormHandler(id);
                                            },
                                        },
                                        {
                                            type: "submit",
                                            disabled: isSubmitting || !isValid,
                                            name: "save",
                                            role: "primary",
                                            hidden: !isAccessPermission,
                                        },
                                    ]}
                                />
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default ProductsSubRow;
